import { useCallback, useRef, useState } from "react";

/**
 * Removes an item from a list and keeps the last removal so it can be undone.
 *
 * One removal can be undone at a time: a new removal replaces the pending one. The pending removal
 * lives in component state, so it goes away with the component.
 *
 * @returns `pendingUndo` (the last removal, or null), `removeWithUndo` (runs `remove` and keeps the
 * removal), `undo` (runs `restore` and clears it) and `dismissUndo` (clears it without restoring).
 */
export function useUndoableRemove() {
  const [pendingUndo, setPendingUndo] = useState<PendingUndo | null>(null);
  const lastRemovalId = useRef(0);

  const removeWithUndo = useCallback(({ message, remove, restore }: UndoableRemoveInput) => {
    remove();
    lastRemovalId.current += 1;
    setPendingUndo({ id: lastRemovalId.current, message, restore });
  }, []);

  const undo = useCallback(() => {
    pendingUndo?.restore();
    setPendingUndo(null);
  }, [pendingUndo]);

  const dismissUndo = useCallback(() => setPendingUndo(null), []);

  return { pendingUndo, removeWithUndo, undo, dismissUndo };
}

interface UndoableRemoveInput {
  /** What was removed, for example "Procedure removed: 99391". */
  message: string;
  /** Removes the item now. */
  remove: () => void;
  /** Puts the item back. Runs when the user clicks Undo. */
  restore: () => void;
}

interface PendingUndo {
  /** Unique per removal, so two removals with the same message still differ. */
  id: number;
  message: string;
  restore: () => void;
}
